import React, { useCallback, useEffect, useRef, useState } from 'react';

import {
  CentroDeCusto,
  FiltrosCompras,
  fetchCentrosDeCusto,
  fetchCompradoresCompras,
} from '../../../api/comprasDashboardApi';
import { notify } from '../../../shared/feedback/notify';
import SelecaoMultiplaModal from './SelecaoMultiplaModal';
import '../../../components/modal/contac/filtro/ModalFiltro.css';
import '../pages/DashboardCompras.css';
import '../../../shared/feedback/aviso-de-tela.css';

/**
 * Filtros do Dashboard de Compras (at-100).
 *
 * Segue o padrão do sistema: montagem condicional (o pai faz `{aberto && <Modal/>}`), estado local
 * que só sai no "Aplicar", e **recebe o que está aplicado** — sem isso o modal reabre em branco e
 * um "Aplicar" seguinte limparia tudo em silêncio (BUG-V43).
 */

interface Props {
  aplicados: FiltrosCompras;
  onAplicar: (filtros: FiltrosCompras) => void;
  onFechar: () => void;
  sidebarRef?: React.RefObject<HTMLElement>;
}

interface OpcaoDeComprador {
  id: string;
  rotulo: string;
}

export default function FiltroComprasModal({ aplicados, onAplicar, onFechar, sidebarRef }: Props) {
  const [centrosCusto, setCentrosCusto] = useState<string[]>(aplicados.centrosCusto ?? []);
  const [centros, setCentros] = useState<CentroDeCusto[] | null>(null);
  const [falhouCentros, setFalhouCentros] = useState(false);
  // O modal de centros, aberto POR CIMA deste. Montagem condicional, como todo modal do projeto.
  const [centrosAberto, setCentrosAberto] = useState(false);
  const [de, setDe] = useState(aplicados.de ?? '');
  const [ate, setAte] = useState(aplicados.ate ?? '');
  const [comprador, setComprador] = useState(aplicados.comprador ?? '');
  // at-109. Estado inicial vindo de `aplicados` como os demais: o modal precisa REABRIR mostrando
  // o que está valendo — sem isso, "Aplicar" logo depois de abrir limparia o filtro em silêncio
  // (BUG-V43).
  const [placa, setPlaca] = useState(aplicados.placa ?? '');
  const [compradores, setCompradores] = useState<OpcaoDeComprador[] | null>(null);
  const [falhouCompradores, setFalhouCompradores] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  /*
   * A lista de compradores do seletor (at-105).
   *
   * 🔴 **Nunca enviar o próprio `comprador` nesta chamada.** A rota é filtrada pela mesma query,
   * então mandá-lo junto faria a lista encolher para um item assim que alguém escolhesse alguém —
   * e não haveria como voltar atrás sem fechar o modal.
   *
   * ⚠️ Usa os centros e o período porque são eles que definem de quem faz sentido falar; o
   * `de`/`ate` aqui são os do rascunho, não os aplicados, para a lista acompanhar o que se está
   * montando.
   */
  const carregarCompradores = useCallback(async () => {
    try {
      const dados = await fetchCompradoresCompras({
        centrosCusto: centrosCusto.length ? centrosCusto : undefined,
        de: de || undefined,
        ate: ate || undefined,
      });
      setCompradores(
        dados.compradores
          // Comprador sem nome continua na lista, com o código: escondê-lo tiraria requisições do
          // filtro sem dizer. Depois da at-105 isso deve ser raro — a cobertura medida foi total.
          .map((c) => ({ id: c.analistaCompra, rotulo: c.nome ?? `Código ${c.analistaCompra.slice(0, 8)}` }))
          // A rota ordena por carga, que é o certo para o gráfico e errado para um campo de busca.
          .sort((a, b) => a.rotulo.localeCompare(b.rotulo, 'pt-BR')),
      );
      setFalhouCompradores(false);
    } catch (e) {
      // 🔴 Lista vazia aqui seria lida como "não há compradores no período" — e quem filtrasse por
      // um deles concluiria que ninguém comprou nada. Estado seguro + aviso (at-60).
      setCompradores(null);
      setFalhouCompradores(true);
      notify.errorFrom(
        e,
        'Não foi possível carregar a lista de compradores. Os demais filtros continuam funcionando.',
      );
    }
  }, [centrosCusto, de, ate]);

  useEffect(() => {
    void carregarCompradores();
  }, [carregarCompradores]);

  /*
   * O cadastro de centros de custo (at-112).
   *
   * ⚠️ **Sem filtro nenhum**, ao contrário da lista de compradores: o cadastro é o universo de
   * opções, e recortá-lo pelo período faria sumir do filtro justamente o centro que o usuário
   * quer investigar num mês em que ele não teve movimento.
   */
  useEffect(() => {
    void (async () => {
      try {
        const dados = await fetchCentrosDeCusto();
        setCentros(dados.centros);
        setFalhouCentros(false);
      } catch (e) {
        // 🔴 Lista vazia seria lida como "não há centros de custo", e quem quisesse filtrar
        // concluiria que o cadastro não existe. Estado seguro + aviso (at-60).
        setCentros(null);
        setFalhouCentros(true);
        notify.errorFrom(
          e,
          'Não foi possível carregar os centros de custo. Os demais filtros continuam funcionando.',
        );
      }
    })();
  }, []);

  useEffect(() => {
    /*
     * 🔴 Com o modal de centros aberto, os dois caminhos ficam INERTES (at-112).
     *
     * O filho é renderizado fora do `ref` deste modal, então o handler de "clicou fora" contaria
     * qualquer clique dentro dele como externo e fecharia o pai no primeiro clique. E os dois
     * listeners de Escape vivem em `document`, são irmãos — `stopPropagation` não separa um do
     * outro. É a armadilha que o `ChecklistModal` documenta desde a at-58, aqui na 5ª ocorrência.
     */
    if (centrosAberto) return undefined;

    const aoClicarFora = (evento: MouseEvent) => {
      const alvo = evento.target as Node;
      if (ref.current?.contains(alvo)) return;
      if (sidebarRef?.current?.contains(alvo)) return;
      onFechar();
    };
    const aoTeclar = (evento: KeyboardEvent) => {
      if (evento.key === 'Escape') onFechar();
    };
    document.addEventListener('mousedown', aoClicarFora);
    document.addEventListener('keydown', aoTeclar);
    return () => {
      document.removeEventListener('mousedown', aoClicarFora);
      document.removeEventListener('keydown', aoTeclar);
    };
  }, [onFechar, sidebarRef, centrosAberto]);

  /** O que o botão diz: o nome quando é um só, a contagem a partir de dois. */
  const rotuloDosCentros =
    centrosCusto.length === 0
      ? 'Todos os centros de custo'
      : centrosCusto.length === 1
        ? (centros ?? []).find((c) => c.pk === centrosCusto[0])?.descricao ?? '1 centro de custo'
        : `${centrosCusto.length} centros de custo selecionados`;

  return (
    <div className="modal-filtro-overlay">
      {/*
        ⚠️ TRÊS níveis, como o molde do Contac (ModalFiltro.tsx): o overlay, o `content` que dá a
        LARGURA (`min(560px, 92vw)`) e o `card` branco, que é `width: 100%` dentro dele.
      
        Fundir os dois no mesmo elemento — o que esta tela fazia até 16/09/2026 — faz o `width:
        100%` do card vencer o `min(560px…)`, porque é declarado depois no CSS, e o modal estica
        de ponta a ponta da tela.
      */}
      <div className="modal-filtro-content" ref={ref}>
        <div className="filtro-modal-card">
        <div className="filtro-modal-header">
          <h2>Filtros</h2>
          <button type="button" className="close-button" onClick={onFechar} aria-label="Fechar">
            ×
          </button>
        </div>

        <div className="filtro-modal-body">
          <section className="filtro-section">
            <h3>Centro de custo</h3>
            {/*
              at-112: caixas de seleção, e não um menu de três opções.

              O eixo do painel deixou de ser "Peças x Outros" (dois valores derivados do local de
              estoque) e passou a ser o centro de custo do ERP — 30 deles, com o nome que a tela do
              Nasajon mostra. Vidro Premium, Oficina Externa Carro e Oficina Interna Moto eram, no
              eixo antigo, a mesma coisa.

              É um MODAL POR CIMA deste, e não uma lista inline: a lista inline foi recusada pelo
              Victor em 22/09/2026 ("esses checkbox estão horríveis"). Modal empilhado tem duas
              metades obrigatórias — a guarda de clique fora/Escape no efeito acima, e o z-index
              explícito do filho (`empilhado`) — e as duas estão aqui.
            */}
            {falhouCentros ? (
              <p className="aviso-de-tela">
                Não foi possível carregar os centros de custo — isto não significa que não haja
                nenhum. Aplicar sem marcar nada mostra o painel inteiro.
              </p>
            ) : null}
            {centros && centros.length <= 1 && !falhouCentros ? (
              <p className="aviso-de-tela">
                O cadastro de centros de custo ainda não foi sincronizado. Até a próxima
                sincronização, o painel mostra a empresa inteira.
              </p>
            ) : null}
            <button
              type="button"
              className="compras-filtro-botao"
              aria-label="Centro de custo"
              disabled={!centros}
              onClick={() => setCentrosAberto(true)}
            >
              {rotuloDosCentros}
            </button>
            <p className="filtro-field-hint">
              Nada marcado: o painel mostra todos os centros. Na lista, o número ao lado de cada um
              é o total de requisições já sincronizadas daquele centro, não o do período escolhido.
            </p>
            {centrosAberto && centros ? (
              <SelecaoMultiplaModal
                titulo="Centros de custo"
                empilhado
                comBusca
                opcoes={centros.map((c) => ({
                  valor: c.pk,
                  rotulo: c.descricao,
                  detalhe: `${c.codigo ? `${c.codigo} · ` : ''}${c.requisicoes.toLocaleString('pt-BR')}`,
                }))}
                selecionados={centrosCusto}
                onAplicar={(escolhidos) => {
                  setCentrosCusto(escolhidos);
                  setCentrosAberto(false);
                }}
                onFechar={() => setCentrosAberto(false)}
                sidebarRef={sidebarRef}
              />
            ) : null}
          </section>

          <section className="filtro-section">
            <h3>Período de cadastro da requisição</h3>
            <div className="filtro-grid-2">
              <label>
                De
                <input
                  type="date"
                  className="premium-text-input"
                  value={de}
                  onChange={(e) => setDe(e.target.value)}
                />
              </label>
              <label>
                Até
                <input
                  type="date"
                  className="premium-text-input"
                  value={ate}
                  onChange={(e) => setAte(e.target.value)}
                />
              </label>
            </div>
            <p className="filtro-field-hint">Sem período, o painel mostra os últimos 30 dias.</p>
          </section>

          <section className="filtro-section">
            <h3>Comprador</h3>
            <div className="select-wrapper">
              <select
                aria-label="Comprador"
                value={comprador}
                onChange={(e) => setComprador(e.target.value)}
                disabled={!compradores}
              >
                <option value="">Todos os compradores</option>
                {(compradores ?? []).map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.rotulo}
                  </option>
                ))}
              </select>
            </div>
            {falhouCompradores ? (
              <p className="filtro-field-hint">
                Não foi possível carregar a lista de compradores — isto não significa que não haja
                nenhum. Os outros filtros continuam funcionando.
              </p>
            ) : (
              <p className="filtro-field-hint">
                Os nomes vêm do próprio Nasajon e acompanham o setor e o período escolhidos acima.
              </p>
            )}
          </section>

          <section className="filtro-section">
            <h3>Placa do veículo</h3>
            <input
              type="text"
              aria-label="Placa"
              className="premium-text-input"
              placeholder="Ex.: KVV9G43"
              value={placa}
              onChange={(e) => setPlaca(e.target.value)}
            />
            <p className="filtro-field-hint">
              A placa vem do texto da requisição e não está em todas elas — os painéis mostram em
              quantas ela foi identificada.
            </p>
          </section>
        </div>

        <div className="filtro-modal-footer">
          <button
            type="button"
            className="filtro-btn-secondary filtro-btn-limpar"
            onClick={() => {
              setCentrosCusto([]);
              setDe('');
              setAte('');
              setComprador('');
              setPlaca('');
            }}
          >
            Limpar filtros
          </button>
          <button type="button" className="filtro-btn-secondary" onClick={onFechar}>
            Cancelar
          </button>
          <button
            type="button"
            className="filtro-btn-primary"
            onClick={() =>
              onAplicar({
                // ⚠️ Lista vazia vira undefined: "nenhum marcado" é "todos", e mandar [] faria o
                // backend montar um recorte vazio.
                centrosCusto: centrosCusto.length ? centrosCusto : undefined,
                de: de || undefined,
                ate: ate || undefined,
                comprador: comprador || undefined,
                // Canoniza aqui também (o backend repete): a URL fica legível e o usuário pode
                // digitar com hífen sem receber lista vazia.
                placa: placa.toUpperCase().replace(/[^A-Z0-9]/g, '') || undefined,
              })
            }
          >
            Aplicar Filtros
          </button>
        </div>
        </div>
      </div>
    </div>
  );
}
